import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { button, notifyIcon, number, quote } from "../../../../config";
import { formatTime, parseTimeStringToDate, reminderLocalMomentFromInstant, reminderLocalMomentTomorrowAtDefaultTime } from "../../../../helper/common";
import { subStr } from "../../../../shared/validators/validator";
import { getNotification } from "../../../../utils/common";
import { onOpen } from "../../../../utils/kendo";
import { useDynamicReminderPopup } from "../../../Tasks/Reminders/taskRemindersHook";
import { handleTaskKeyUpdate } from "../../../Tasks/tasks.service";
import { menuItems } from "./commentConstants";

/**
 * Submenu for "Remind me" (comments) or task bell when the task has no reminders yet.
 * Parents: CommentDropdown, TaskReminder
 * @param {*} param0
 * @author Sarthak
 */
const ReminderMenu = ({
  setShowDrop,
  setShowRemindMenu,
  setShowCustomPopup,
  comment,
  anchor,
  handleMouseDown,
  handleMouseUp,
  entityId,
  entityType,
  isTaskReminder = false,
  appendTo,
  popupClassName,
  menuAnchorAlign,
  menuPopupAlign,
  keepParentOpenOnCustom = false,
}) => {
  const { task } = useSelector((state) => state.taskSidebar);
  const reminderType = isTaskReminder ? number.ONE : number.FIVE;
  const messageSource = isTaskReminder ? task?.Name : comment;
  const { createReminder } = useDynamicReminderPopup({
    setShowReminder: setShowRemindMenu,
    reminderType,
    commentText: messageSource,
    entityId,
    entityType,
  });
  const { UserReminderTime } = useSelector((state) => state.auth.user);
  const formattedTime = formatTime(UserReminderTime, "HH:mm");
  let defaultTime = parseTimeStringToDate(formattedTime);

  const menuRef = useRef(null);
  const anchorAlign = menuAnchorAlign ?? { horizontal: "left", vertical: "bottom" };
  const popupAlign = menuPopupAlign ?? { horizontal: "right", vertical: "middle" };
  const popupClass =
    popupClassName ?? `dt-popup reminder-menu-popup${isTaskReminder ? " task-bell-reminder-menu" : " mr-2"}`;

  /**
   * Creates the reminder based on chosen option
   * @author {Sarthak Arora}
   */
  const handleItemClick = async (key) => {
    setShowRemindMenu(false);
    if (key !== number.FOUR || !keepParentOpenOnCustom) {
      setShowDrop(false);
    }
    const oneHourLater = reminderLocalMomentFromInstant(Date.now() + 60 * 60 * 1000);
    const threeHoursLater = reminderLocalMomentFromInstant(Date.now() + 3 * 60 * 60 * 1000);
    const tomorrow = reminderLocalMomentTomorrowAtDefaultTime(defaultTime);
    const reminderText = subStr(messageSource || "", 0, 120);
    let sendSuccessNotification = false;
    let reminderResponse;
    switch (key) {
      case number.ONE: {
        reminderResponse = await createReminder({
          selectedTime: oneHourLater,
          reminderDate: oneHourLater,
          reminderMessage: reminderText,
          reminderType,
        });
        if (reminderResponse?.Id) sendSuccessNotification = true;
        break;
      }
      case number.TWO: {
        reminderResponse = await createReminder({
          selectedTime: threeHoursLater,
          reminderDate: threeHoursLater,
          reminderMessage: reminderText,
          reminderType,
        });
        if (reminderResponse?.Id) sendSuccessNotification = true;
        break;
      }
      case number.THREE: {
        reminderResponse = await createReminder({
          selectedTime: defaultTime,
          reminderDate: tomorrow,
          reminderMessage: reminderText,
          reminderType,
        });
        if (reminderResponse?.Id) sendSuccessNotification = true;
        break;
      }
      case number.FOUR: {
        setShowCustomPopup(true);
        break;
      }
    }

    if (reminderResponse?.Id && isTaskReminder && task?.taskId) {
      await handleTaskKeyUpdate(false, "reminderCount", task.reminderCount + number.ONE);
    }

    if (sendSuccessNotification && key !== number.FOUR) {
      getNotification(quote.REMINDER_SET, notifyIcon.SUCCESS_ICON);
    } else if (key !== number.FOUR) getNotification(quote.FAILED_TO_ADD_REMINDER, notifyIcon.ERROR_ICON);
  };

  return (
    <Popup
      anchor={anchor.current}
      appendTo={appendTo}
      show={true}
      onOpen={() => onOpen(anchor.current)}
      popupClass={popupClass}
      popupAlign={popupAlign}
      anchorAlign={anchorAlign}
      collision={{
        horizontal: "fit",
        vertical: "fit",
      }}>
      <div ref={menuRef} tabIndex={number.ONE} className='dt-popup-body comment-actions' onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
        {isTaskReminder && <div className='reminder-menu-task-heading font-weight-bold'>{button.REMIND_ME}</div>}
        {menuItems.map((item) => (
          <div id={`reminder-menu-item-${item.key}`} key={item.key} className='cursor-pointer reminder-menu-item' onClick={() => handleItemClick(item.key)}>
            {item.text}
          </div>
        ))}
      </div>
    </Popup>
  );
};

export default ReminderMenu;
